import { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { VB_TOKENS, VB_FONTS } from "./tokens";
import { clamp, lerp } from "./mathUtils";
import { useReducedMotion } from "./clock";
import { Atmosphere, CameraBreathe } from "./Atmosphere";
import { VaultDoor } from "./VaultDoor";
import { AshDissolve } from "./Particles";
import { computeRoundState, VB_DEFAULT_CASHOUT_T } from "./computeRoundState";
import { formatMultiplier, formatCryptoCompact } from "../../lib/format.js";

// =============================================================================
// SceneBust — production composition for the BUST cinematic.
//
// Maps wall-clock t (0..duration) onto a 4-second slice (13.0 → 17.0s) of the
// sequencer round timeline so the player sees the 5th key strike, the door
// swing AWAY, and the dissolve into ash.
//
// Source ref: Scenes.jsx VaultStage (lines 8-178) + ResultBanner (180-223).
// =============================================================================

export type SceneAudioCueBust = "keyStrike" | "lockCrack" | "doorSwing" | "bustThud";

export interface SceneBustProps {
  multiplier: number;
  /** Wall-clock time within the scene, 0..duration. */
  t: number;
  /** Total wall duration in seconds. */
  duration: number;
  width: number;
  height: number;
  vaultSize?: number;
  /** Player's stake in the round currency (defaults to 0.001 BTC). */
  stake?: number;
  /** Currency code shown in the banner subtitle. */
  currency?: string;
  onAudioCue?: (cue: SceneAudioCueBust) => void;
}

export function SceneBust({
  multiplier,
  t,
  duration,
  width,
  height,
  vaultSize = 420,
  stake = 0.001,
  currency = "BTC",
  onAudioCue,
}: SceneBustProps): JSX.Element {
  // ALL hooks must be called unconditionally on every render. `useReducedMotion`
  // flips from false→true after mount when the OS prefers-reduced-motion is
  // enabled; if we returned early on `reduced` BEFORE the useRef/useEffect
  // below, render 1 (reduced=false) would call 3 hooks and render 2
  // (reduced=true) would call 1 — React error #300 ("Rendered fewer hooks
  // than expected"). Keep all hook calls at the top; gate the side-effect
  // body on `reduced` instead.
  const reduced = useReducedMotion();
  const firedRef = useRef<Set<SceneAudioCueBust>>(new Set());
  const T = VB_TOKENS;

  // Map wall time to round time. Reduced-motion freezes on the peak frame
  // (door ~70% away, ash already dissolving); animated path slides 13→17.
  const localT = reduced
    ? 15.5
    : lerp(13.0, 17.0, clamp(duration > 0 ? t / duration : 0, 0, 1));
  const state = computeRoundState(localT, "bust", VB_DEFAULT_CASHOUT_T);

  // Post-patch the multiplier so the banner shows the actual cashout-attempt
  // mult the player was reaching for when they busted.
  state.multiplier = multiplier;

  // ----- Audio cue dispatch (fire each cue exactly once per mount) -----
  // Skip in reduced-motion mode — the static frame has no soundtrack.
  useEffect(() => {
    if (reduced) return;
    const fired = firedRef.current;
    const k5Angle = state.keyAngles[4] ?? 0;
    if (k5Angle > 0.05 && !fired.has("keyStrike")) {
      fired.add("keyStrike");
      onAudioCue?.("keyStrike");
    }
    if (k5Angle > 0.95 && !fired.has("lockCrack")) {
      fired.add("lockCrack");
      onAudioCue?.("lockCrack");
    }
    if (state.openProgress > 0.05 && !fired.has("doorSwing")) {
      fired.add("doorSwing");
      onAudioCue?.("doorSwing");
    }
    if (state.showResult === "bust" && !fired.has("bustThud")) {
      fired.add("bustThud");
      onAudioCue?.("bustThud");
    }
  }, [reduced, state.keyAngles, state.openProgress, state.showResult, onAudioCue]);

  // Reduced-motion peak frame: door ~70% away, ash already dissolving.
  if (reduced) {
    return (
      <div
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          background: T.void0,
          filter: "saturate(0.6) brightness(0.85)",
          width,
          height,
        }}
        data-reduced-motion="true"
      >
        <VaultDoor state={state} size={vaultSize} />
        <ResultBanner multiplier={multiplier} progress={1} stake={stake} currency={currency} />
      </div>
    );
  }

  // ----- Banner appearance progress (slide-up over 0.5s, starting at 15.0) -----
  const bannerProgress = state.showResult === "bust"
    ? clamp((localT - 15.0) / 0.5, 0, 1)
    : 0;

  const outerStyle: CSSProperties = {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    background: T.void0,
    filter: state.desaturate ? "saturate(0.15) brightness(0.85)" : "none",
    transition: "filter 200ms",
    width,
    height,
  };

  const haloStyle: CSSProperties = {
    position: "absolute",
    left: "50%",
    top: "50%",
    width: vaultSize * 1.6,
    height: vaultSize * 1.6,
    transform: "translate(-50%, -50%)",
    background: state.openMode === "bust"
      ? `radial-gradient(circle, ${T.cyan3}66, ${T.cyan1}22 30%, transparent 60%)`
      : `radial-gradient(circle, ${T.steel4}33, transparent 60%)`,
    filter: "blur(40px)",
    opacity: state.openMode ? state.openProgress : 0.55,
    pointerEvents: "none",
  };

  const bustFlashStyle: CSSProperties = {
    position: "absolute",
    inset: 0,
    background: `radial-gradient(circle, ${T.red2}aa, transparent 60%)`,
    mixBlendMode: "screen",
    opacity: state.bustFlash,
    pointerEvents: "none",
  };

  return (
    <div style={outerStyle} data-scene="bust">
      <Atmosphere
        t={localT}
        tension={state.tension}
        lensX={0.5}
        lensY={0.45}
        hue="cool"
      />

      <CameraBreathe t={localT} intensity={0.7 + state.tension * 0.5}>
        {/* Halo behind the door */}
        <div style={haloStyle} />

        {/* The vault door */}
        <VaultDoor state={state} size={vaultSize} />

        {/* Bust red flash */}
        {state.bustFlash > 0 && <div style={bustFlashStyle} />}

        {/* Ash dissolve once the door cracks open ~30%. Sibling Particles API
            requires `t`, `centerX`, `centerY` — flagged in handoff summary. */}
        {state.openProgress > 0.3 && (
          <AshDissolve
            progress={(state.openProgress - 0.3) * 1.4}
            t={localT}
            centerX={width / 2}
            centerY={height / 2}
            count={100}
          />
        )}
      </CameraBreathe>

      {/* Result banner — top of the modal so the cyan halo doesn't bury it. */}
      {bannerProgress > 0 && (
        <ResultBanner
          multiplier={multiplier}
          progress={bannerProgress}
          stake={stake}
          currency={currency}
        />
      )}
    </div>
  );
}

// ----- Result banner -----
// Sits at top of the cinematic with a soft scrim so the cyan halo doesn't
// bury it. Stake is parameterized so the line reflects the player's bet.
interface ResultBannerProps {
  multiplier: number;
  /** 0..1 — slide-in / fade-in progress. */
  progress: number;
  stake: number;
  currency: string;
}

function ResultBanner({ multiplier, progress, stake, currency }: ResultBannerProps): JSX.Element {
  const T = VB_TOKENS;
  const yOffset = (1 - progress) * 20;
  const style: CSSProperties = {
    position: "absolute",
    left: "50%",
    top: "12%",
    transform: `translate(-50%, ${yOffset}px)`,
    opacity: progress,
    pointerEvents: "none",
    textAlign: "center",
    padding: "14px 32px",
    borderRadius: 14,
    background: "linear-gradient(180deg, rgba(6,8,15,0.55), rgba(6,8,15,0))",
    backdropFilter: "blur(2px)",
    filter: `drop-shadow(0 0 24px ${T.red2}) drop-shadow(0 0 40px ${T.red2}aa)`,
  };
  const labelStyle: CSSProperties = {
    fontFamily: VB_FONTS.ui,
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: 6,
    color: T.red3,
  };
  const multStyle: CSSProperties = {
    fontFamily: VB_FONTS.display,
    fontSize: 64,
    lineHeight: 1,
    marginTop: 6,
    color: T.red2,
    letterSpacing: -1,
  };
  const subStyle: CSSProperties = {
    fontFamily: VB_FONTS.mono,
    fontSize: 12,
    marginTop: 6,
    color: T.red3,
  };
  return (
    <div style={style}>
      <div style={labelStyle}>VAULT SEALED</div>
      <div style={multStyle}>{formatMultiplier(multiplier)}</div>
      <div style={subStyle}>−{formatCryptoCompact(stake, currency)} {currency}</div>
    </div>
  );
}
